'use client'

import Link from 'next/link'
import {ActivityCover} from '@fitro/customer-ui/ActivityCover'
import {CalendarDays, MapPin, UserRound, UsersRound} from 'lucide-react'
import {formatEventSessionDisplay} from '@fitro/date-time/display'
import type {PublicScheduleEvent} from '../../../services/public-schedule.service'
import {useTranslation} from '../../../hooks/useTranslation'
import {useAuth} from '@fitro/customer-host/auth'
import {getClassCoverGradient} from '../../../lib/classColors'
import {formatInstructorNames} from '../../../lib/utils'
import {buildAuthHref} from './publicEntryLinks'
import {PublicEventDetailsLink} from './PublicEventDetailsLink'
import {PublicGuestBookingChooser} from './PublicGuestBookingChooser'
import {
    getPublicEventBookingMode,
    getAuthenticatedEventDetailsHref,
    getPublicEventDetailsHref,
    getPublicEventGuestCheckoutHref,
} from './publicEventLinks'
import {Avatar} from '../../ui/Avatar'
import {resolvePublicAssetUrl} from './publicAssetUrl'

const ACTION_CLASS_NAME = 'inline-flex min-h-11 w-full items-center justify-center rounded-[8px] bg-[linear-gradient(9deg,var(--landing-primary-600)_0%,var(--landing-secondary-500)_100%)] px-4 text-[14px] font-semibold text-white transition-[filter,scale] duration-press ease-out active:scale-[0.97] motion-reduce:active:scale-100 hover:brightness-[1.08] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--landing-focus-ring)] focus-visible:ring-offset-2'

type PublicEventsContentProps = {
    events: PublicScheduleEvent[]
}

function PublicEventCard({event}: {event: PublicScheduleEvent}) {
    const {t, dateTime, formatCurrency} = useTranslation('landing')
    const {isLoaded, isSignedIn} = useAuth()
    const start = new Date(event.startDateTime)
    const end = new Date(event.endDateTime)
    const session = formatEventSessionDisplay(dateTime, start, end)
    const title = event.className?.trim() || t('publicEntry.events.fallbackTitle')
    const description = event.description?.trim()
    const location = event.location?.name?.trim() || t('publicEntry.events.noLocation')
    const instructor = formatInstructorNames(event.instructors, '').trim() || t('publicEntry.events.noInstructor')
    const instructorItems = event.instructors
        .map((item, index) => ({
            id: `${item.firstName ?? ''}-${item.lastName ?? ''}-${index}`,
            name: [item.firstName, item.lastName].filter(Boolean).join(' ').trim(),
            avatarUrl: item.avatarUrl,
        }))
        .filter(item => item.name)
    const instructorAvatars = instructorItems.filter(item => item.avatarUrl)
    const price = typeof event.priceMinor === 'number'
        ? event.priceMinor === 0
            ? t('publicEntry.events.free')
            : formatCurrency(event.priceMinor / 100, {currency: 'PLN'})
        : '—'
    const signedIn = Boolean(isLoaded && isSignedIn)
    const bookingMode = getPublicEventBookingMode(event, signedIn)
    const authenticatedDetailsHref = getAuthenticatedEventDetailsHref(event)
    const actionHref = signedIn
        ? authenticatedDetailsHref
        : buildAuthHref('/sign-in', authenticatedDetailsHref)
    const publicDetailsHref = signedIn
        ? authenticatedDetailsHref
        : getPublicEventDetailsHref(event)
    const imageUrl = resolvePublicAssetUrl(event.classCoverImageUrl)
    const availabilityKey = event.availabilityStatus === 'last_spots'
        ? 'lastSpots'
        : event.availabilityStatus === 'registration_closed'
            ? 'registrationClosed'
            : event.availabilityStatus

    const reserveLabel = t('publicEntry.cta.reserveEvent', {
        className: title,
        date: session.primaryLabel,
        time: session.secondaryLabel,
    })
    // A guest who chose to book goes straight to the form on the event page.
    const action = bookingMode === 'guest' ? (
        <Link href={getPublicEventGuestCheckoutHref(event) ?? actionHref} aria-label={reserveLabel} className={ACTION_CLASS_NAME}>
            {t('publicEntry.cta.reserve')}
        </Link>
    ) : bookingMode === 'waitlist' ? (
        <PublicGuestBookingChooser className={ACTION_CLASS_NAME} event={event} mode="waitlist" signInHref={actionHref}>
            {t('publicEntry.guestCheckout.waitlist')}
        </PublicGuestBookingChooser>
    ) : bookingMode === 'account' && signedIn ? (
        // Signed in, one full load opens the event in the app.
        <a href={actionHref} aria-label={reserveLabel} className={ACTION_CLASS_NAME}>
            {t('publicEntry.cta.reserve')}
        </a>
    ) : bookingMode === 'account' ? (
        <Link href={actionHref} aria-label={reserveLabel} className={ACTION_CLASS_NAME}>
            {t('publicEntry.cta.reserve')}
        </Link>
    ) : event.availabilityStatus === 'full' ? (
        <span className="inline-flex min-h-11 w-full cursor-not-allowed items-center justify-center rounded-[8px] border border-[var(--landing-neutral-100)] bg-[var(--landing-neutral-50)] px-4 text-[14px] font-semibold text-[var(--landing-neutral-500)]">
            {t('publicEntry.schedule.availability.full')}
        </span>
    ) : null

    return (
        <article className="group relative overflow-hidden rounded-[14px] border border-[var(--landing-neutral-100)] bg-white shadow-[0_12px_28px_rgba(57,98,198,0.07)]">
            <PublicEventDetailsLink
                href={publicDetailsHref}
                ariaLabel={t('publicEntry.details.open', {name: title})}
                className="absolute inset-0 z-0 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--landing-focus-ring)]"
            />
            <div className="pointer-events-none relative z-[1] grid min-w-0 lg:min-h-[225px] lg:grid-cols-[360px_minmax(0,1fr)_230px]">
                <div
                    data-testid="event-cover"
                    className="w-full self-stretch overflow-hidden"
                    style={{background: getClassCoverGradient(null, event.id ?? title)}}
                >
                    <ActivityCover src={imageUrl} background={getClassCoverGradient(null, event.id ?? title)}/>
                </div>

                <div className="min-w-0 p-5 md:px-6 md:py-5">
                    <h2 className="line-clamp-2 break-words text-[21px] font-bold leading-tight text-[var(--landing-neutral-900)] md:text-[24px]">{title}</h2>
                    {description ? <p className="mt-2 line-clamp-2 text-[14px] leading-6 text-[var(--landing-neutral-500)] md:text-[15px]">{description}</p> : null}
                    <div className="mt-4 grid gap-2.5 text-[14px] text-[var(--landing-neutral-500)] sm:grid-cols-2 lg:grid-cols-1">
                        <div className="flex min-w-0 items-center gap-2">
                            {instructorAvatars.length > 0 ? (
                                <span className="flex shrink-0 -space-x-1" aria-hidden="true">
                                    {instructorAvatars.map(item => (
                                        <Avatar key={item.id} name={item.name} src={item.avatarUrl ?? undefined} size="small" className="h-[22px] w-[22px] text-[9px] ring-2 ring-white"/>
                                    ))}
                                </span>
                            ) : (
                                <UserRound className="h-[18px] w-[18px] shrink-0 text-[var(--landing-primary-500)]"/>
                            )}
                            <span className="truncate">{instructor}</span>
                        </div>
                        <p className="flex min-w-0 items-center gap-2"><MapPin className="h-[18px] w-[18px] shrink-0 text-[var(--landing-primary-500)]"/><span className="truncate">{location}</span></p>
                        <p className="flex min-w-0 items-center gap-2"><UsersRound className="h-[18px] w-[18px] shrink-0 text-[var(--landing-primary-500)]"/><span>{t(`publicEntry.schedule.availability.${availabilityKey}`)}</span></p>
                    </div>

                    <div className="mt-5 border-t border-[var(--landing-neutral-100)] pt-4 lg:hidden">
                        <div className="mb-4 flex items-center justify-between gap-4">
                            <p className="flex min-w-0 flex-wrap items-baseline gap-x-1.5 gap-y-0.5 font-bold leading-5 text-[var(--landing-neutral-900)]">
                                <span className="whitespace-nowrap">{session.primaryLabel}</span>
                                <span className={session.isCrossDay ? 'whitespace-nowrap' : 'whitespace-nowrap text-[14px] font-normal text-[var(--landing-neutral-500)]'}>
                                    {session.isCrossDay ? <><span aria-hidden="true">{session.secondaryPrefix}</span><span className="sr-only">{t('publicEntry.events.rangeTo')} </span></> : null}{session.secondaryLabel}
                                </span>
                            </p>
                            <p className="self-center font-bold text-[var(--landing-neutral-900)]">{price}</p>
                        </div>
                        <div className="pointer-events-auto relative z-[2]">{action}</div>
                    </div>
                </div>

                <div className="hidden border-l border-[var(--landing-neutral-100)] bg-[var(--landing-neutral-50)] px-5 py-4 lg:flex lg:flex-col">
                    <div>
                        <p className="flex items-center gap-2 text-[12px] font-medium uppercase tracking-[0.04em] text-[var(--landing-neutral-500)]"><CalendarDays className="h-4 w-4"/>{t('publicEntry.events.date')}</p>
                        <p className="mt-1 flex min-w-0 flex-wrap items-baseline gap-x-1.5 gap-y-0.5 font-bold leading-5 text-[var(--landing-neutral-900)]">
                            <span className="whitespace-nowrap">{session.primaryLabel}</span>
                            <span className={session.isCrossDay ? 'whitespace-nowrap' : 'whitespace-nowrap text-[14px] font-normal text-[var(--landing-neutral-500)]'}>
                                {session.isCrossDay ? <><span aria-hidden="true">{session.secondaryPrefix}</span><span className="sr-only">{t('publicEntry.events.rangeTo')} </span></> : null}{session.secondaryLabel}
                            </span>
                        </p>
                    </div>
                    <div className="mt-4">
                        <p className="text-[12px] font-medium uppercase tracking-[0.04em] text-[var(--landing-neutral-500)]">{t('publicEntry.events.price')}</p>
                        <p className="mt-1 whitespace-nowrap text-[20px] font-bold text-[var(--landing-neutral-900)]">{price}</p>
                    </div>
                    <div className="pointer-events-auto relative z-[2] mt-auto pt-4">{action}</div>
                </div>
            </div>
        </article>
    )
}

export function PublicEventsContent({events}: PublicEventsContentProps) {
    const {t} = useTranslation('landing')
    const sortedEvents = [...events].sort((left, right) => {
        const startDifference = Date.parse(left.startDateTime) - Date.parse(right.startDateTime)
        if (startDifference !== 0) return startDifference
        const endDifference = Date.parse(left.endDateTime) - Date.parse(right.endDateTime)
        if (endDifference !== 0) return endDifference
        return left.id.localeCompare(right.id)
    })

    return (
        <section aria-labelledby="public-events-content">
            <h2 id="public-events-content" className="sr-only">{t('publicEntry.events.title')}</h2>
            <div className="space-y-4">
                {sortedEvents.map((event) => (
                    <PublicEventCard key={event.id} event={event} />
                ))}
            </div>
        </section>
    )
}
